import { ChangeDetectorRef, Directive, EventEmitter, OnDestroy, OnInit, Output } from "@angular/core";
import { User, userToString } from "src/services/user/user.interface";
import { firstValueFrom, Observable, Subscription } from "rxjs";
import { BaseFormFieldComponent } from "../base-form-field.component";
import { SearchbarCustomEvent } from "../../../../../shared/ion-components-events.interface";
import { Driver } from "src/services/driver/driver.interface";
import { map } from "rxjs/operators";

@Directive()
export abstract class BaseSearchFormFieldComponent<T extends Driver | User>
  extends BaseFormFieldComponent<string>
  implements OnInit, OnDestroy
{
  public isSearching = false;
  @Output() public onValueClick = new EventEmitter<void>();
  public searchInput: string | undefined;
  public searchResults: T[] = [];
  public readonly userToString = userToString;
  private searchSubscription?: Subscription;

  protected constructor(private readonly cdr: ChangeDetectorRef) {
    super();
  }

  public ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
  }

  public async ngOnInit(): Promise<void> {
    this.fieldValue = this.initialValue;
    super.ngOnInit();
  }

  public async searchInputChanged(event: unknown): Promise<void> {
    this.searchSubscription?.unsubscribe();

    const eventCast = event as SearchbarCustomEvent;
    const queryString = eventCast.detail.value;

    if (!queryString) {
      this.searchResults = [];
      this.isSearching = false;
      return;
    }

    // Forcing change detection to render results right away. With @angular/fire 21.0.0-rc.0, something makes the
    // rerender very slow to happen. Might be a bug.
    this.isSearching = true;
    this.cdr.detectChanges();

    this.searchSubscription = this.search(queryString)
      .pipe(map((results) => results.slice(0, 5)))
      .subscribe((results) => {
        this.searchResults = results;
        this.isSearching = false;
        this.cdr.detectChanges();
      });
  }

  public async selectSearchResult(input: T): Promise<void> {
    this.searchResults = [];
    this.searchInput = undefined;
    this.isSearching = false;

    const value = this.getStorableValue(input);
    await this.writeValue(value);
    await this.onInputChangeInternal(value);
  }

  public async clearSearch(): Promise<void> {
    this.isSearching = false;
    await this.writeValue("");
  }

  public abstract toDisplayString(input: T | undefined): string;

  protected async toDisplayValue(input: string): Promise<string> {
    if (!input) {
      return this.getNoValueString();
    }

    const result = await firstValueFrom(this.getById(input));
    return this.toDisplayString(result);
  }

  protected getStorableValue(input: T): string {
    return input.id;
  }

  protected abstract getNoValueString(): string;
  protected abstract getById(id: string): Observable<T | undefined>;
  protected abstract search(input: string): Observable<T[]>;
}
